import { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { Trophy, Mic, Camera } from "lucide-react";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_matte-gold-elite/artifacts/inhgs69v_580B7C33-5489-440E-9DE5-5AB4B720A08A.png";

const Header = () => {
  const navigate = useNavigate();

  const handleNavClick = (sectionId) => {
    // If we're not on home page, navigate there first
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className="luxury-header">
      <Link to="/" className="luxury-logo-container">
        <img src={LOGO_URL} alt="Elite Dream Agency" className="luxury-logo-img" />
      </Link>
      <nav className="luxury-nav">
        <button onClick={() => handleNavClick('home')} className="luxury-nav-link">HOME</button>
        <button onClick={() => handleNavClick('about')} className="luxury-nav-link">ABOUT</button>
        <button onClick={() => handleNavClick('pillars')} className="luxury-nav-link">PILLARS</button>
        <button 
          className="luxury-cta-btn"
          onClick={() => navigate('/talent-intake')}
        >
          Join the Legacy
        </button>
      </nav>
    </header>
  );
};

const HeroSection = () => {
  return (
    <section id="home" className="hero-section">
      <div className="hero-background"></div>
      <div className="hero-content">
        <h1 className="hero-headline">
          Turning Your Dreams Into{" "}
          <span>
            Legacies
            <span className="sparkle sparkle-1">✦</span>
            <span className="sparkle sparkle-2">✦</span>
            <span className="sparkle sparkle-3">✦</span>
          </span>
        </h1>
        <p className="hero-subheadline">
          Recognizing and showcasing the next generation of icons in Barbados
        </p>
      </div>
    </section>
  );
};

const PillarsSection = () => {
  const pillars = [
    {
      icon: <Trophy size={56} />,
      title: "Sports",
      description: "Professional media branding and highlight production for athletes."
    },
    {
      icon: <Mic size={56} />,
      title: "Entertainment",
      description: "Visual storytelling and brand scaling for artists and performers."
    },
    {
      icon: <Camera size={56} />,
      title: "Lifestyle",
      description: "Content creation for luxury brands and Caribbean cultural icons."
    }
  ];

  return (
    <section id="pillars" className="pillars-section">
      <h2 className="section-title">The Pillars</h2>
      <p className="section-subtitle">
        Three core pillars drive our mission to create legacies
      </p>
      <div className="pillars-grid">
        {pillars.map((pillar, index) => (
          <div key={index} className="pillar-card">
            <div className="pillar-icon">{pillar.icon}</div>
            <h3 className="pillar-title">{pillar.title}</h3>
            <p className="pillar-description">{pillar.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="about-section">
      <div className="about-content">
        <h2 className="section-title">About Elite Dream Agency</h2>
        <p className="about-text">
          Elite Dream Agency is Barbados' premier talent scouting and content creation house. We are the bridge between raw local potential and global legacy.
        </p>
        <a 
          href="https://customer-assets.emergentagent.com/job_matte-gold-elite/artifacts/flp6euqg_Christopher%20Taitt.vcf"
          download="Christopher Taitt.vcf"
          className="download-contact-btn"
        >
          Download Contact Card
        </a>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="luxury-footer">
      <div className="footer-content">
        <div className="footer-brand">
          <img src={LOGO_URL} alt="Elite Dream Agency" className="footer-logo-img" />
          <p className="footer-tagline">
            Leading the way in Sports, Entertainment, and Lifestyle content creation. Based in Barbados.
          </p>
        </div>
        <div className="footer-contact">
          <h4 className="footer-section-title">Contact</h4>
          <div className="footer-contact-item">
            <a href="mailto:christaitt@elitedreamagency.com">christaitt@elitedreamagency.com</a>
          </div>
          <div className="footer-contact-item">
            <a href="tel:+12462847262">+1 (246) 284-7262</a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        © {new Date().getFullYear()} Elite Dream Agency. All rights reserved.
      </div>
    </footer>
  );
};

const Home = () => {
  return (
    <div>
      <HeroSection />
      <PillarsSection />
      <AboutSection />
      <Footer />
    </div>
  );
};

const TalentIntake = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    category: '',
    socialHandle: '',
    dream: ''
  });
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Create FormData for file upload
      const formDataObj = new FormData();
      formDataObj.append('fullName', formData.fullName);
      formDataObj.append('category', formData.category);
      formDataObj.append('socialHandle', formData.socialHandle);
      formDataObj.append('dream', formData.dream);
      
      if (file) {
        formDataObj.append('portfolio', file);
      }

      const response = await axios.post(`${BACKEND_URL}/api/talent`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSubmitted(true);
        setFormData({
          fullName: '',
          category: '',
          socialHandle: '',
          dream: ''
        });
        setFile(null);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.response?.data?.detail || 'An error occurred while submitting your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="talent-form-page">
      <div className="talent-form-container">
        <h1 className="form-title">Join the Legacy</h1>
        <p className="form-subtitle">
          Tell us about your dreams and let's build your legacy together
        </p>
        
        {!submitted ? (
          <form className="talent-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                value={formData.fullName}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                name="category"
                className="form-select"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select a category</option>
                <option value="Sports">Sports</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Lifestyle">Lifestyle</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Social Media Handle</label>
              <input
                type="text"
                name="socialHandle"
                className="form-input"
                value={formData.socialHandle}
                onChange={handleChange}
                required
                placeholder="@yourusername (Instagram/TikTok)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">The Dream</label>
              <textarea
                name="dream"
                className="form-textarea"
                value={formData.dream}
                onChange={handleChange}
                required
                placeholder="Tell us about your legacy..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Upload Photo or Portfolio</label>
              <input
                type="file"
                className="form-file-input"
                onChange={handleFileChange}
                accept="image/*"
              />
            </div>

            <button type="submit" className="form-submit-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        ) : (
          <div className="success-message">
            <h2 className="success-title">Welcome to the Legacy</h2>
            <p className="success-text">
              Thank you for submitting your application. We'll review your information and get back to you soon.
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/talent-intake" element={<TalentIntake />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;